<template>
  <!--
    客户详情（只读）。

    ★ 与客户编辑页是两回事：编辑页在 /customer/list/detail，本页在 /customer/list/info。
      分开是为了不动已有入口 —— 消息、其它页面都可能指到编辑页那个地址。
  -->
  <div class="defaultViewStyle customer-info">
    <list-page>
      <!-- 头部：客户名 + 往类型/云客户两个标识 -->
      <div class="ci-header">
        <el-page-header @back="goBack" :content="dataDetail.merchantRemarkName || '客户详情'"/>
        <div class="ci-tags">
          <el-tag v-if="contactTypeName(dataDetail.contactType)" size="small">{{ contactTypeName(dataDetail.contactType) }}</el-tag>
          <!-- 只有是云客户才标出来，非云客户标一个「云客户：否」没有意义 -->
          <el-tag v-if="Number(dataDetail.cloudType) === CLOUD_TYPE.YES" size="small" type="success">云客户</el-tag>
        </div>
      </div>
      <list-table background-color="#FFF" :show-shadow="false" :show-border="false" style="overflow: hidden">
        <el-tabs v-model="activeName">
          <!-- ① 基本信息：编辑页的全部字段，这里只读展示 -->
          <el-tab-pane label="基本信息" name="base">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="客户名称">{{ dataDetail.merchantRemarkName || '—' }}</el-descriptions-item>
              <el-descriptions-item label="联系电话">{{ dataDetail.merchantRemarkPhone || '—' }}</el-descriptions-item>
              <el-descriptions-item label="电子信箱">{{ dataDetail.merchantRemarkEmail || '—' }}</el-descriptions-item>
              <el-descriptions-item label="往来类型">{{ contactTypeName(dataDetail.contactType) || '—' }}</el-descriptions-item>
              <el-descriptions-item label="客户类型">{{ dictName(customerTypeList, dataDetail.customerType) || '—' }}</el-descriptions-item>
              <el-descriptions-item label="所属行业">{{ dictName(industryList, dataDetail.industryId) || '—' }}</el-descriptions-item>
              <el-descriptions-item label="是否云客户">{{ cloudTypeName(dataDetail.cloudType) || '—' }}</el-descriptions-item>
              <el-descriptions-item label="软件类型">{{ dictName(softTypeList, dataDetail.softType) || '—' }}</el-descriptions-item>
              <el-descriptions-item label="客户来源">{{ dictName(sourceList, dataDetail.sourceDictId) || '—' }}</el-descriptions-item>
              <el-descriptions-item label="联系人">{{ dataDetail.merchantRemarkContact || '—' }}</el-descriptions-item>
              <el-descriptions-item label="累计金额(元)">{{ money(dataDetail.accumulatedAmount) }}</el-descriptions-item>
              <el-descriptions-item label="客户分类">{{ categoryName || '—' }}</el-descriptions-item>
              <el-descriptions-item label="负责人">{{ dataDetail.ownerEmployeeNames || '—' }}</el-descriptions-item>
              <el-descriptions-item label="地区">
                <administrative v-if="dataDetail.merchantRegion" v-model="dataDetail.merchantRegion" :disabled="true"/>
                <span v-else>—</span>
              </el-descriptions-item>
              <el-descriptions-item label="性别">{{ genderName(dataDetail.merchantGender) || '—' }}</el-descriptions-item>
              <el-descriptions-item label="生日">{{ dateOnly(dataDetail.merchantBirthday) }}</el-descriptions-item>
              <el-descriptions-item label="下单数量">{{ dataDetail.orderCount || 0 }}</el-descriptions-item>
              <el-descriptions-item label="客户积分">{{ dataDetail.point || 0 }}</el-descriptions-item>
              <el-descriptions-item label="创建时间">{{ dateTime(dataDetail.createdTime) }}</el-descriptions-item>
              <el-descriptions-item label="联系地址" :span="2">{{ dataDetail.merchantAddress || '—' }}</el-descriptions-item>
              <el-descriptions-item label="银行账号">{{ dataDetail.merchantBankAccount || '—' }}</el-descriptions-item>
              <el-descriptions-item label="银行户名">{{ dataDetail.merchantBankAccountName || '—' }}</el-descriptions-item>
              <el-descriptions-item label="开户行">{{ dataDetail.merchantBankName || '—' }}</el-descriptions-item>
              <el-descriptions-item label="税号">{{ dataDetail.merchantTaxNumber || '—' }}</el-descriptions-item>
              <el-descriptions-item label="备注" :span="2">{{ dataDetail.merchantRemark || '—' }}</el-descriptions-item>
            </el-descriptions>
            <!-- 企业微信环境才显示：查看/绑定/解绑企业微信客户 -->
            <form-items :columns="2" style="margin-top: 12px">
              <partner-wx-bind :partner-id="dataDetail.partnerId"/>
            </form-items>
          </el-tab-pane>

          <!-- ② 关联记录：联系人 + 销售合同（销售合同暂占位） -->
          <el-tab-pane label="关联记录" name="related">
            <form-tag label="客户联系人"/>
            <yq-table v-if="contactList.length" :data="contactList" height="auto" page-name="customerContactInfo" app-name="basedata">
              <yq-table-column label="联系人名称" prop="merchantRemarkName" min-width="140rem">
                <template v-slot="{row}">
                  {{ row.merchantRemarkName }}
                  <el-tag v-if="Number(row.keyContact) === KEY_CONTACT.YES" size="small" type="warning">关键人</el-tag>
                </template>
              </yq-table-column>
              <yq-table-column label="联系电话" prop="merchantRemarkPhone" min-width="120rem"/>
              <yq-table-column label="职位" prop="customerPosition" min-width="120rem">
                <template v-slot="{row}">{{ dictName(positionList, row.customerPosition) || '—' }}</template>
              </yq-table-column>
              <yq-table-column label="性别" prop="merchantGender" width="60rem">
                <template v-slot="{row}">{{ genderName(row.merchantGender) || '—' }}</template>
              </yq-table-column>
              <yq-table-column label="生日" prop="merchantBirthday" min-width="120rem">
                <template v-slot="{row}">{{ dateOnly(row.merchantBirthday) }}</template>
              </yq-table-column>
              <yq-table-column label="地区" prop="merchantRegion" min-width="180rem">
                <template v-slot="{row}">
                  <administrative v-if="row.merchantRegion" v-model="row.merchantRegion" :disabled="true"/>
                  <span v-else>—</span>
                </template>
              </yq-table-column>
              <yq-table-column label="联系地址" prop="merchantAddress" min-width="180rem"/>
            </yq-table>
            <el-empty v-else description="暂无联系人"/>

            <!-- 合同模块只在 CRM 系统提供；其它系统不展示入口，避免跳到不存在的路由 -->
            <template v-if="isCrmMode">
            <form-tag label="销售合同">
              <page-button type="write" el-type="success" @click="toNewContract">新建合同</page-button>
            </form-tag>
            <yq-table v-if="contractList.length" :data="contractList" height="auto" page-name="customerContractInfo" app-name="basedata">
              <yq-table-column label="合同编号" prop="contractNumber" min-width="170rem">
                <template v-slot="{row}">
                  <a @click="toContractDetail(row)">{{ row.contractNumber || '—' }}</a>
                </template>
              </yq-table-column>
              <yq-table-column label="合同名称" prop="contractName" min-width="200rem" :show-overflow-tooltip="true"/>
              <yq-table-column label="合同类型" prop="contractType" width="110rem" align="center">
                <template v-slot="{row}">{{ contractTypeName(row.contractType) }}</template>
              </yq-table-column>
              <yq-table-column label="合同金额(元)" prop="amount" width="130rem" align="right">
                <template v-slot="{row}">{{ money(row.amount) }}</template>
              </yq-table-column>
              <yq-table-column label="已回款(元)" prop="amountReceived" width="130rem" align="right">
                <template v-slot="{row}">{{ money(row.amountReceived) }}</template>
              </yq-table-column>
              <yq-table-column label="签约日期" prop="billDate" width="120rem" align="center">
                <template v-slot="{row}">{{ dateOnly(row.billDate) }}</template>
              </yq-table-column>
              <yq-table-column label="到期日期" prop="expireDate" width="120rem" align="center">
                <template v-slot="{row}">{{ dateOnly(row.expireDate) }}</template>
              </yq-table-column>
              <yq-table-column label="状态" prop="contractStatus" width="100rem" align="center">
                <template v-slot="{row}">
                  <el-tag size="small" :type="contractStatusOf(row.contractStatus).type">
                    {{ contractStatusOf(row.contractStatus).text }}
                  </el-tag>
                </template>
              </yq-table-column>
              <yq-table-column label="操作" prop="options" width="90rem" align="center">
                <template v-slot="{row}">
                  <table-button @click="toContractDetail(row)">查看</table-button>
                </template>
              </yq-table-column>
            </yq-table>
            <el-empty v-else description="暂无销售合同"/>
            </template>
          </el-tab-pane>

          <!-- ③ 跟进动态：card 列表，不分页。后端已按跟进时间倒序返回 -->
          <el-tab-pane label="跟进动态" name="follow">
            <div v-if="followList.length" class="ci-follow">
              <div class="ci-follow-item" v-for="f in followList" :key="f.id">
                <div class="ci-follow-head">
                  <span class="who">{{ f.followEmployeeName || '—' }}</span>
                  <span class="time">{{ dateOnly(f.followTime) }}</span>
                </div>
                <div class="ci-follow-content">{{ f.followContent }}</div>
                <div class="ci-follow-next" v-if="f.nextFollowDate">下次跟进：{{ dateOnly(f.nextFollowDate) }}</div>
              </div>
            </div>
            <el-empty v-else description="暂无跟进动态"/>
          </el-tab-pane>
        </el-tabs>
      </list-table>
    </list-page>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import FormTag from "@/components/h5/layout/FormTag.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import PartnerWxBind from "@/components/h5/partner/PartnerWxBind.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import Administrative from "@/components/h5/data/Administrative.vue";
import customerApi from "@/api/normal/common/customerApi";
import customerFollowApi from "@/api/normal/common/customerFollowApi";
import commonApi from "@/api/commonApi";
import contractApi from "@/api/dz-crm/partner/contractApi";
import {contractStatusOf, contractTypeName} from "@/views/h5/crm/contract/contractDictionary";
import {
  CLOUD_TYPE, DICT_CUSTOMER_POSITION, DICT_CUSTOMER_SOFT_TYPE, DICT_CUSTOMER_TYPE, DICT_INDUSTRY_TYPE,
  DICT_PARTNER_ORIGIN, KEY_CONTACT, cloudTypeName, contactTypeName, dictName
} from "@/views/h5/crm/crmDictionary";
import {useRoute, useRouter} from "vue-router";

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "CustomerInfo",
  components: {Administrative, PageButton, TableButton, PartnerWxBind, FormItems, FormTag, ListTable, ListPage},
  data() {
    return {
      activeName: "base",
      dataDetail: {},
      contactList: [],
      followList: [],
      // 该客户名下的销售合同
      contractList: [],
      isCrmMode: process.env.VUE_APP_SYSTEM_CODE === "CRM",
      // 字典：后端只返回值，名称由前端关联
      customerTypeList: [],
      industryList: [],
      softTypeList: [],
      sourceList: [],
      positionList: [],
      // 客户分类是树，展示名称要按 dictCode 在树里找
      categoryTree: [],
      // 常量暴露给模板用
      CLOUD_TYPE, KEY_CONTACT,
    };
  },
  computed: {
    /** 客户分类名称：分类存的是 dictCode，在分类树里按 code 找名称 */
    categoryName() {
      return this.findCategoryName(this.categoryTree, this.dataDetail.partnerCategory);
    },
  },
  beforeRouteLeave(to, from, next) {
    // 本页只有 query.id 一个入参，不缓存页面数据
    next();
  },
  created() {
    this.initDictionary();
    if (this.route.query.id) {
      this.findById(this.route.query.id);
    }
  },
  methods: {
    dictName,
    contactTypeName,
    cloudTypeName,
    contractTypeName,
    contractStatusOf,
    initDictionary() {
      commonApi.queryUniqueDictionaryList(DICT_CUSTOMER_TYPE).then(list => this.customerTypeList = list || []);
      commonApi.queryUniqueDictionaryList(DICT_INDUSTRY_TYPE).then(list => this.industryList = list || []);
      commonApi.queryUniqueDictionaryList(DICT_CUSTOMER_SOFT_TYPE).then(list => this.softTypeList = list || []);
      commonApi.queryUniqueDictionaryList(DICT_PARTNER_ORIGIN).then(list => this.sourceList = list || []);
      // 联系人的职位字典
      commonApi.queryUniqueDictionaryList(DICT_CUSTOMER_POSITION).then(list => this.positionList = list || []);
      commonApi.queryDictionaryTree("CUSTOMER_CATEGORY").then(list => this.categoryTree = list || []);
    },
    async findById(id) {
      const data = await customerApi.findById(id);
      this.dataDetail = data || {};
      this.contactList = (data && data.contactList) || [];
      // 跟进动态挂在往来单位档案id上（不是 merchant 行主键），与商机跟进共用一张表
      if (this.dataDetail.partnerId) {
        this.followList = await customerFollowApi.queryByCustomer(this.dataDetail.partnerId).catch(() => []) || [];
        if (this.isCrmMode) {
          this.loadContracts();
        }
      }
    },
    /** 该客户的销售合同（合同 customerId 存的是往来单位档案id） */
    async loadContracts() {
      if (!this.dataDetail.partnerId) {
        this.contractList = [];
        return;
      }
      const res = await contractApi.queryList({
        customerId: this.dataDetail.partnerId,
        pageNumber: 1,
        pageSize: 500,
      }).catch(() => null);
      this.contractList = (res && res.records) || [];
    },
    toNewContract() {
      this.router.push(`/contract/list/edit?customerId=${this.dataDetail.partnerId}`);
    },
    toContractDetail(row) {
      this.router.push(`/contract/list/detail?id=${row.id}`);
    },
    goBack() {
      this.router.back();
    },
    findCategoryName(tree, code) {
      if (!code) {
        return "";
      }
      for (const node of tree || []) {
        if (String(node.dictCode) === String(code)) {
          return node.dictName;
        }
        const name = this.findCategoryName(node.children, code);
        if (name) {
          return name;
        }
      }
      return "";
    },
    genderName(gender) {
      const n = Number(gender);
      if (n === 1) return "男";
      return n === 2 ? "女" : "";
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
    dateTime(value) {
      return value ? String(value).slice(0, 19) : "—";
    },
    money(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/el-tabs";

.customer-info {
  height: 100%;

  .ci-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 0 12px;

    .ci-tags {
      display: flex;
      gap: 8px;
    }
  }

  // 跟进动态：card 列表，不分页
  .ci-follow {
    padding: 10px 0;
  }

  .ci-follow-item {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 10px;

    .ci-follow-head {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 12px;
      color: #909399;

      .who {
        color: #303133;
        font-weight: 600;
      }

      .time {
        margin-left: auto;
      }
    }

    .ci-follow-content {
      margin-top: 6px;
      font-size: 13px;
      color: #606266;
      line-height: 1.6;
      white-space: pre-wrap;
      word-break: break-all;
    }

    .ci-follow-next {
      margin-top: 6px;
      font-size: 12px;
      color: var(--el-color-primary);
    }
  }

  :deep(.el-empty) {
    padding: 20px 0;
  }
  :deep(.el-tabs) {
    .el-tabs__content .el-tab-pane{
      overflow: auto;
    }
  }
}
</style>
